<template>
  <v-card>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="cancel()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $lang("companies.cards.subscription_plan.title") }}</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12" xs="12" md="6">
            <v-card outlined>
              <v-card-title>{{ $lang('companies.cards.subscription_plan.title') }}</v-card-title>
              <v-card-text>
                <v-date
                  v-model="subscription.expired_at"
                  :label="$lang('companies.subscription_plan.fields.expired_at')"
                  :error="!!validation.expired_at"
                  :error-messages="!!validation.expired_at ? validation.expired_at : []"
                  :disabled="isDisableEdit"
                  dense
                />
                <v-select
                  v-model="subscription.type"
                  :items="$in('ConstCompanySubscriptionPlanTypes')"
                  :label="$lang('companies.subscription_plan.fields.type')"
                  :error="!!validation.type"
                  :error-messages="!!validation.type ? validation.type : []"
                  :disabled="isDisableEdit"
                  dense
                />
                
                <v-row>
                  <v-col cols="4">
                    <v-text-field
                      v-if="$const('ConstCompanySubscriptionPlanTypes', 'FIXED') === subscription.type"
                      v-model="subscription.amount"
                      :label="$lang('companies.subscription_plan.fields.amount')"
                      :suffix="`EUR`"
                      :error="!!validation.amount"
                      :error-messages="!!validation.amount ? validation.amount : []"
                      :disabled="isDisableEdit"
                      clearable
                      dense
                    />
                    <v-text-field
                      v-if="$const('ConstCompanySubscriptionPlanTypes', 'VARIABLE') === subscription.type"
                      v-model="subscription.amount"
                      :label="$lang('companies.subscription_plan.fields.amount')"
                      :suffix="$lang('units.singular')"
                      :error="!!validation.amount"
                      :error-messages="!!validation.amount ? validation.amount : []"
                      :disabled="isDisableEdit"
                      clearable
                      dense
                    />
                    <v-text-field
                      v-if="$const('ConstCompanySubscriptionPlanTypes', 'PERCENTAGE') === subscription.type"
                      v-model="subscription.amount"
                      :label="$lang('companies.subscription_plan.fields.amount')"
                      :suffix="`%`"
                      :error="!!validation.amount"
                      :error-messages="!!validation.amount ? validation.amount : []"
                      :disabled="isDisableEdit"
                      clearable
                      dense
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-select
                      v-model="subscription.period"
                      :items="periods"
                      :label="$lang('companies.subscription_plan.fields.period')"
                      :suffix="$lang('main.words.months')"
                      :error="!!validation.period"
                      :error-messages="!!validation.period ? validation.period : []"
                      :disabled="isDisableEdit"
                      dense
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field :label="$lang('companies.subscription_plan.fields.amount')"
                      :value="amountPerYear"
                      disabled
                      :suffix="`EUR/${$lang('main.words.year')}`"
                      dense
                    />
                  </v-col>
                </v-row>
                <v-text-field
                  v-model="subscription.credit"
                  :label="$lang('companies.subscription_plan.fields.credit')"
                  :suffix="$lang('main.words.minutes')"
                  :error="!!validation.credit"
                  :error-messages="!!validation.credit ? validation.credit : []"
                  clearable
                  dense
                />
                <v-text-field
                  v-model="subscription.vat"
                  :label="$lang('companies.subscription_plan.fields.vat_amount')"
                  :suffix="`%`"
                  :error="!!validation.vat"
                  :error-messages="!!validation.vat ? validation.vat : []"
                  :disabled="isDisableEdit"
                  dense
                />
                <v-select
                  v-if="subscription.vat == 0"
                  v-model="subscription.vat_exclusion"
                  :items="$in('ConstInvoiceVatExclusionIT')"
                  :label="$lang('companies.subscription_plan.fields.vat_exclusion')"
                  :error="!!validation.vat_exclusion"
                  :error-messages="!!validation.vat_exclusion ? validation.vat_exclusion : []"
                  :disabled="isDisableEdit"
                  dense
                />
                <v-text-field
                  v-if="$can('INTERNAL_SUPPORT')"
                  v-model="subscription.trial_period"
                  type="number"
                  :label="$lang('companies.subscription_plan.fields.trial_period')"
                  :suffix="$lang('main.words.days')"
                  :error="!!validation.trial_period"
                  :error-messages="!!validation.trial_period ? validation.trial_period : []"
                  :disabled="isDisableEdit"
                  dense
                />
                <v-boolean
                  v-model="subscription.is_auto_renew"
                  :label="$lang('companies.subscription_plan.fields.is_auto_renew')"
                  :error="!!validation.is_auto_renew"
                  :error-messages="!!validation.is_auto_renew ? validation.is_auto_renew : []"
                  dense
                />
                <v-expansion-panels flat accordion :readonly="isDisableEdit">
                  <v-expansion-panel>
                    <v-expansion-panel-header class="text-h6">
                      {{ $lang('companies.subscription_plan.fields.notes') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-textarea
                        v-model="subscription.notes"
                        filled
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header
                      class="text-h6"
                    >{{ $lang('companies.subscription_plan.features.title') }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col>
                          <v-text-field
                            v-model="subscription.features.units_count"
                            :label="$lang('companies.subscription_plan.features.units_count')"
                            :error="!!validation['features.units_count']"
                            :error-messages="!!validation['features.units_count'] ? validation['features.units_count'] : []"
                            dense
                          />
                          <v-text-field
                            v-model="subscription.features.connections_count"
                            :label="$lang('companies.subscription_plan.features.connections_count')"
                            :error="!!validation['features.connections_count']"
                            :error-messages="!!validation['features.connections_count'] ? validation['features.connections_count'] : []"
                            dense
                          />
                          <v-boolean
                            v-model="subscription.features.has_channel_manager"
                            :label="$const('ConstSubscriptionFeatures', 'CHANNEL_MANAGER', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_accounting"
                            :label="$const('ConstSubscriptionFeatures', 'ACCOUNTING', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_booking_engine"
                            :label="$const('ConstSubscriptionFeatures', 'BOOKING_ENGINE', true)"
                            dense
                          />
                          <v-text-field
                            v-if="!!subscription.features.has_booking_engine"
                            v-model="subscription.features.booking_engine_count"
                            :label="$lang('companies.subscription_plan.features.booking_engine_count')"
                            :error="!!validation['features.booking_engine_count']"
                            :error-messages="!!validation['features.booking_engine_count'] ? validation['features.booking_engine_count'] : []"
                            dense
                          />
                          <v-boolean
                            v-model="subscription.features.has_website_builder"
                            :label="$const('ConstSubscriptionFeatures', 'WEBSITE_BUILDER', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_legal_compliance"
                            :label="$const('ConstSubscriptionFeatures', 'LEGAL_COMPLIANCE', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_messaging"
                            :label="$const('ConstSubscriptionFeatures', 'MESSAGING', true)"
                            dense
                            hide-details
                          />
                        </v-col>
                        <v-col>
                          <v-boolean
                            v-model="subscription.features.has_reports"
                            :label="$const('ConstSubscriptionFeatures', 'REPORTS', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_cmms"
                            :label="$const('ConstSubscriptionFeatures', 'CMMS', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_revenue"
                            :label="$const('ConstSubscriptionFeatures', 'REVENUE_MANAGEMENT', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_housekeeping"
                            :label="$const('ConstSubscriptionFeatures', 'HOUSEKEEPING', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_quotes"
                            :label="$const('ConstSubscriptionFeatures', 'QUOTES', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_contracts"
                            :label="$const('ConstSubscriptionFeatures', 'CONTRACTS', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_google_sheets"
                            :label="$const('ConstSubscriptionFeatures', 'GOOGLE_SHEETS', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_vacation_rental"
                            :label="$const('ConstSubscriptionFeatures', 'VACATION_RENTAL', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_smart_devices"
                            :label="$const('ConstSubscriptionFeatures', 'SMART_DEVICES', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_resources"
                            :label="$const('ConstSubscriptionFeatures', 'RESOURCES', true)"
                            dense
                            hide-details
                          />
                          <v-boolean
                            v-model="subscription.features.has_sdi"
                            :label="$const('ConstSubscriptionFeatures', 'SDI', true)"
                            dense
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Altro
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-select
                        v-model="subscription.sla"
                        :items="$in('ConstServiceLevelAgreements')"
                        :label="$lang('companies.subscription_plan.fields.sla')"
                        :error="!!validation.sla"
                        :error-messages="!!validation.sla ? validation.sla : []"
                      />
                      <v-select
                        v-model="subscription.package"
                        :items="$in('ConstSubscriptionPackages')"
                        :label="$lang('companies.subscription_plan.fields.package')"
                        :error="!!validation.package"
                        :error-messages="!!validation.package ? validation.package : []"
                      />
                      <v-text-field
                        v-model="subscription.onboarding_amount"
                        type="number"
                        :label="$lang('companies.subscription_plan.fields.onboarding_amount')"
                        suffix="EUR"
                        :error="!!validation.onboarding_amount"
                        :error-messages="!!validation.onboarding_amount ? validation.onboarding_amount : []"
                      />
                      <v-text-field
                        v-model="subscription.onboarding_discount"
                        type="number"
                        :label="$lang('companies.subscription_plan.fields.onboarding_discount')"
                        suffix="%"
                        :error="!!validation.onboarding_discount"
                        :error-messages="!!validation.onboarding_discount ? validation.onboarding_discount : []"
                      />
                      <v-text-field
                        v-model="subscription.training_amount"
                        type="number"
                        :label="$lang('companies.subscription_plan.fields.training_amount')"
                        suffix="EUR"
                        :error="!!validation.training_amount"
                        :error-messages="!!validation.training_amount ? validation.training_amount : []"
                      />
                      <v-text-field
                        v-model="subscription.training_discount"
                        type="number"
                        :label="$lang('companies.subscription_plan.fields.training_discount')"
                        suffix="%"
                        :error="!!validation.training_discount"
                        :error-messages="!!validation.training_discount ? validation.training_discount : []"
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-card-actions>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        class="ma-2"
                        v-if="$can('INTERNAL_SUPPORT')"
                      >
                        <v-icon>mdi-signature</v-icon>
                        Contratto
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        @click="openContractSignConfirmation"
                        :loading="sendContractRequest.loading"
                      >
                        <v-list-item-title>
                          <v-icon class="mr-1">mdi-signature-freehand</v-icon>
                          Invia Contratto
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item
                        @click="getContract"
                        :loading="getContractRequest.loading"
                        :disabled="!subscription.e_signature_contract_id"
                      >
                        <v-list-item-title>
                          <v-icon class="mr-1">mdi-signature-freehand</v-icon>
                          Recupera Contratto
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="!subscription.id"
                    color="primary"
                    @click="submit"
                    :loading="loading"
                    :disabled="isDisableEdit"
                  >{{ $lang('main.crud.save') }}</v-btn>
                  <v-btn
                    v-if="subscription.id && !isDisableEdit"
                    color="primary"
                    @click="submit"
                    :loading="loading"
                  >{{ $lang('main.crud.update') }}</v-btn>
                  <template v-if="!!subscription.id">
                    <v-btn
                      v-if="!subscription.is_active"
                      color="success"
                      @click="enable"
                      :loading="replaceRequest.loading"
                    >{{ $lang('main.crud.enable') }}</v-btn>
                    <v-btn
                      v-else
                      color="error"
                      @click="openDisable"
                      :loading="replaceRequest.loading"
                    >{{ $lang('main.crud.disable') }}</v-btn>
                    <v-btn
                      v-if="$can('INTERNAL_SUPPORT')"
                      color="error"
                      @click="deleteSubscription"
                      :loading="deletionRequest.loading"
                    >{{ $lang('main.crud.remove') }}</v-btn>
                  </template>
                </v-card-actions>
              </v-card-text>
              <v-card-text v-if="$can('INTERNAL_SUPPORT')" class="admin">
                <v-alert v-if="subscription.notes" type="warning">{{ subscription.notes }}</v-alert>
                <v-row>
                  <v-col cols="12" class="title">CHARGEBEE</v-col>
                  <v-col cols="6">
                    <v-btn @click="syncChargeBee(false)" block :loading="syncChargeBeeRequest.loading" color="warning">AVVIA SUB</v-btn>
                    Crea sub e pagamento
                  </v-col>
                  <v-col cols="6">
                    <v-btn @click="syncChargeBee(true)" block :loading="syncChargeBeeRequest.loading" :disabled="!!subscription.chargebee_id" color="success">IMPORT</v-btn>
                    Importa sub senza avviare pagamenti
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-text v-if="$can('INTERNAL_SUPPORT')" class="admin">
                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="subscription.chargebee_id"
                      label="Chargebee ID"
                      dense
                    />
                    <v-text-field
                      v-model="subscription.stripe_payment_method_id"
                      label="Stripe Payment Method ID"
                      dense
                    />
                    <v-text-field
                      v-model="subscription.stripe_customer_id"
                      label="Stripe Customer ID"
                      dense
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="subscription.created_at"
                      :label="$lang('main.crud.created_at')"
                      dense
                    />
                    <v-text-field
                      v-model="subscription.updated_at"
                      :label="$lang('main.crud.updated_at')"
                      dense
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" xs="12" md="6" v-if="$can('EDIT_COMPANIES_SUBSCRIPTIONS_PM')">
            <v-card v-if="!!subscription.e_signature_contract_id" outlined>
              <v-card-title>{{ $lang('companies.subscription_plan.contract.title') }}</v-card-title>
              <v-card-text>
                <v-btn @click="getContract" :loading="getContractRequest.loading">
                  {{ $lang('companies.subscription_plan.contract.retrieve') }}
                </v-btn>
              </v-card-text>
            </v-card>
            <v-tabs v-model="tab" background-color="transparent" grow>
              <v-tab
                href="#tab-stripe"
              >{{ $lang('companies.subscription_plan.providers.stripe.title') }}</v-tab>
              <!-- <v-tab
                href="#tab-paypal"
              >{{ $lang('companies.subscription_plan.providers.paypal.title') }}</v-tab> -->
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item value="tab-stripe">
                <company-subscription-stripe
                  :company-id="companyId"
                  :company="company"
                  :subscription="subscription"
                  @refresh="retrieveCompany"
                  :highlight-payment="highlightPayment"
                />
              </v-tab-item>
              <v-tab-item value="tab-paypal">
                <company-subscription-pay-pal :company-id="companyId"></company-subscription-pay-pal>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" xs="12" md="12">
            <h2 class="mb-2">{{ $lang('companies.payments.title') }}</h2>
            <CompanyPaymentsList
              v-if="companyId"
              :options="{
                companyId: companyId,
                subscriptionId: subscription.id,
                canCharge: !!subscription.stripe_payment_method_id,
              }"
              :with-company="false"
              :with-filters="false"
            />
            <h2 class="mb-2">{{ $lang('company-invoices.title') }}</h2>
            <CompanyInvoicesList
              :companyId="companyId"
              :withFilters="false"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-confirmation
      v-model="isOpenDisable"
      :width="600"
      :message="disableMessage"
      :options="{
        loading: replaceRequest.loading,
        yes: () => disable()
      }">
    </v-confirmation>
    <v-confirmation
      v-model="isOpenContractSignConfirmation"
      :width="600"
      message="Sei sicuro di voler inviare il contratto per la firma?"
      :options="{
        loading: sendContractRequest.loading,
        yes: () => sendContract()
      }">
    </v-confirmation>
    <v-dialog v-model="isOpenContractData">
      <v-card v-if="isOpenContractData">
        <v-card-title v-if="getContractRequest.data.data && $can('INTERNAL_SUPPORT')">
          Status: {{ getContractRequest.data.data.status }}
        </v-card-title>
        <v-card-text v-if="getContractRequest.data.data && $can('INTERNAL_SUPPORT')">
          <p>Signers: {{ getContractRequest.data.data.signers }}</p>
        </v-card-text>
        <v-card-actions>
          <p v-if="getContractRequest.data.data.status === 'signed'">
            <v-btn :href="getContractRequest.data.data.contract_pdf_url" target="_blank">Visualizza Contratto</v-btn>
          </p>
          <p v-else>
            <v-btn :href="getContractRequest.data.data.signers[0].sign_page_url" target="_blank">
              {{ $lang('companies.subscription_plan.contract.sign')}}
            </v-btn>
          </p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import formable from "@/mixins/formable";


import CompanySubscriptionStripe from "@/pages/companies/subscription-providers/Stripe";
import CompanySubscriptionPayPal from "@/pages/companies/subscription-providers/PayPal";
const CompanyPaymentsList = () => import("@/pages/company-payments/List.vue");
const CompanyInvoicesList = () => import("@/pages/company-invoices/List.vue");

import {
	GET_COMPANIES_SINGLE,
} from "@/store/companies/actions";

import {
	CREATE_SUBSCRIPTION_PLAN,
	CREATE_SUBSCRIPTION_PLAN_RESET,
	UPDATE_SUBSCRIPTION_PLAN,
	UPDATE_SUBSCRIPTION_PLAN_RESET,
	REPLACE_SUBSCRIPTION_PLAN,
	// REPLACE_SUBSCRIPTION_PLAN_RESET,
	// GET_SUBSCRIPTION_PLAN_STRIPE_PAYMENT_METHOD,
	// CONFIRM_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT,
	// CANCEL_SUBSCRIPTION_PLAN_STRIPE_SETUP_INTENT,
  REMOVE_SUBSCRIPTION_PLAN,
  SEND_CONTRACT,
  GET_CONTRACT,
  SYNC_CHARGE_BEE,
} from "@/store/company-subscription/actions";

export default {
	name: "SubscriptionPlanForm",
	components: {
		CompanySubscriptionStripe,
		CompanySubscriptionPayPal,
    CompanyPaymentsList,
    CompanyInvoicesList,
	},
	mixins: [
		formable
	],
  props: {
    companyId: {
      required: true,
    },
    highlightPayment: {
      type: Boolean,
      default: false
    }
  },
	data() {
		return {
			tab: 'tab-stripe',
			subscription: {
        type: null,
        trial_period: 0,
				features: {
          units_count: -1,
          connections_count: 3,
          booking_engine_count: 1,
          has_booking_engine: 1,
          has_accounting: 0,
          has_legal_compliance: 1,
          has_messaging: 1,
          has_cmms: 1,
          has_housekeeping: 1,
          has_quotes: 1,
          has_vacation_rental: 0,
          has_google_sheets: 0,
          has_website: 0,
          has_resources: 0,
        },
        skip_contract: true,
			},
      periods: [1, 2, 3, 4, 6, 8, 12],
      isOpenDisable: false,
      isOpenContractSignConfirmation: false,
      isOpenContractData: false,
		}
	},
	beforeMount() {
		this.subscription.type = this.$const('ConstCompanySubscriptionPlanTypes', 'FIXED');

		this.$store.dispatch(CREATE_SUBSCRIPTION_PLAN_RESET);
		this.$store.dispatch(UPDATE_SUBSCRIPTION_PLAN_RESET);
    this.retrieveCompany();
	},
	methods: {
    retrieveCompany() {
      this.$store.dispatch(GET_COMPANIES_SINGLE, {
        id: this.companyId,
        params: {
          _data: {
            relations: {
              subscription_plan: {
                selects: ['*']
              },
              parent: {
                relations: {
                  subscription_plan: {
                    selects: [
                      'company_id',
                      'features',
                    ]
                  },
                }
              }
            }
          }
        }
      }).then(() => {
        if (this.company.subscription_plan) {
          this.subscription = this.company.subscription_plan;
          return;
        }
        if (this.company.parent && this.company.parent.subscription_plan) {
          this.subscription.features = this.company.parent.subscription_plan.features;
        }
      });
    },
		enable() {
			this.$store.dispatch(REPLACE_SUBSCRIPTION_PLAN, {
				id: this.subscription.id,
				params: {
					is_active: this.$const('ConstBoolean', 'TRUE'),
				}
			}).then(() => {
				this.subscription.is_active = this.$const('ConstBoolean', 'TRUE');
			});
		},
		disable() {
			this.$store.dispatch(REPLACE_SUBSCRIPTION_PLAN, {
				id: this.subscription.id,
				params: {
					is_active: this.$const('ConstBoolean', 'FALSE'),
				}
			}).then(() => {
        this.isOpenDisable = false;
				this.subscription.is_active = this.$const('ConstBoolean', 'FALSE');
			});
    },
    openDisable() {
      this.isOpenDisable = true;
    },
		submit() {
			let data = {
				...this.subscription,
        company_id: this.companyId
			};

			if (this.company.subscription_plan) {
				this.$store.dispatch(UPDATE_SUBSCRIPTION_PLAN, {
					id: this.company.subscription_plan.id,
					params: data
				}).then((subscription) => {
					this.subscription = subscription;
					this.showNotification(this.$lang("main.crud.success_update"), "success");
				});
			} else {
				this.$store.dispatch(CREATE_SUBSCRIPTION_PLAN, data).then((subscription) => {
					// this.createIBAN();
					this.subscription = subscription;
					this.showNotification(this.$lang("main.crud.success_create"), "success");
				});
			}
		},
    openContractSignConfirmation() {
      this.isOpenContractSignConfirmation = true;
    },
    getContract() {
      this.$store.dispatch(GET_CONTRACT, {
        id: this.company.subscription_plan.id
      }).then(response => {
        this.isOpenContractData = true;
      });
    },
    sendContract() {
      if (!this.company.subscription_plan.id) {
        return;
      }

      this.$store.dispatch(SEND_CONTRACT, {
        id: this.company.subscription_plan.id,
      }).then(response => {
        this.retrieveCompany();
        this.isOpenContractSignConfirmation = false;
      })
    },
    deleteSubscription() {
      this.$store.dispatch(REMOVE_SUBSCRIPTION_PLAN, {
        id: this.company.subscription_plan.id
      });
      this.cancel();
    },
    syncChargeBee(isImport = false) {
      if (!this.company.subscription_plan.company_id) return;

      this.$store.dispatch(SYNC_CHARGE_BEE, {
        company_id: this.company.subscription_plan.company_id,
        is_import: isImport,
        is_create: !isImport
      })
    }
	},
	computed: {
		initialized() {
			return !this.companyRequest.loading;
		},
		loading() {
			return this.company.subscription_plan ? this.editingRequest.loading : this.creationRequest.loading;
		},
		validation() {
			return this.company.subscription_plan ? this.editingRequest.validation : this.creationRequest.validation;
		},
		...mapState({
			company: state => state.companies.getSingleRequest.item,
			companyRequest: state => state.companies.getSingleRequest,
			editingRequest: state => state.companySubscription.editingRequest,
			creationRequest: state => state.companySubscription.creationRequest,
			replaceRequest: state => state.companySubscription.replaceRequest,
      deletionRequest: state => state.companySubscription.deletionRequest,
      sendContractRequest: state => state.companySubscription.sendContractRequest,
      getContractRequest: state => state.companySubscription.getContractRequest,
      syncChargeBeeRequest: state => state.companySubscription.syncChargeBeeRequest,
    }),
    disableMessage() {
      if (this.subscription.type === this.$const('ConstCompanySubscriptionPlanTypes', 'PERCENTAGE')) {
        return this.$lang('companies.subscription_plan.disable_percentage');
      }

      return this.$lang('companies.subscription_plan.disable');
    },
    isDisableEdit() {
      return this.$cannot('EDIT_COMPANIES_SUBSCRIPTIONS')
    },  
    amountPerYear() {
      if (!this.subscription.amount) return 0;

      return (parseFloat(this.subscription.amount) / this.subscription.period * 12).toFixed(2);
    }
	},
}
</script>
